import * as THREE from 'three';
import { MILITARY_SPECS, type MilitaryVehicleKind } from '../render/military-vehicles';
import type { Place } from '../world/city';
import type { Military, Soldier } from './military';
import type { TrafficVehicle } from './population';

interface SupportUnit {
  car: TrafficVehicle;
  crew: Soldier[];
  stops: Place[];
  stop: number;
  state: 'dispatch' | 'travelling' | 'working' | 'boarding' | 'mobilised' | 'unavailable';
  target?: THREE.Vector3;
  task?: string;
  taskProgress?: { point: THREE.Vector3; at: number };
  avoidPlace?: string;
  retry: number;
  sequence: number;
  visits: number;
  reason: string;
}
/** Eight distinct finite support vehicles share 74 of the existing 400 base
 * personnel. Trips terminate at occupied, damageable facility interactions. */
export class MilitarySupport {
  units: SupportUnit[] = [];
  stock = new Map<number, number>();
  delivered = 0;
  private epoch = 0;
  constructor(readonly army: Military) {}
  clear(): void {
    for (const u of this.units)
      for (const soldier of u.crew)
        this.army.population.civil?.cancelTask(soldier.person, 'Support fleet lifecycle cleared');
    this.units = [];
    this.stock.clear();
    this.delivered = 0;
  }
  *resetSteps(): Generator<void> {
    this.clear();
    const { city, physics, population } = this.army,
      layout = city.strategic!,
      civil = population.civil!,
      network = population.network;
    this.epoch = physics.life.epoch;
    const parcel = layout.parcels.find((p) => p.id === 'military')!;
    const kinds: MilitaryVehicleKind[] = [
      'military-sedan',
      'military-sedan',
      'apc',
      'apc',
      'tank',
      'tank',
      'troop-truck',
      'troop-truck',
    ];
    let personIndex = 526;
    const servicePlace = (id: number) =>
      city.places!.filter(
        (p) =>
          p.buildingId === id &&
          ['desk', 'workbench', 'maintenance', 'staff'].includes(p.kind) &&
          civil.network.available(p),
      );
    const headquarters = servicePlace(layout.command),
      stores = servicePlace(layout.armoury),
      clinic = servicePlace(layout.clinic);
    this.stock.set(layout.armoury, 64);
    this.stock.set(layout.clinic, 8);
    for (const [i, kind] of kinds.entries()) {
      const spec = MILITARY_SPECS[kind];
      let spawn: { point: THREE.Vector3; yaw: number; from: number } | undefined;
      for (let attempt = 0; attempt < parcel.roads.length * 6; attempt++) {
        const edge = network.edges.get(parcel.roads[(i * 7 + attempt) % parcel.roads.length])!;
        if (!edge.restricted || !network.open(edge, true, spec.length)) continue;
        const from = attempt % 2 ? edge.to : edge.from,
          at = network.waypoint(edge, from, 0.16 + (Math.floor(attempt / parcel.roads.length) % 3) * 0.26),
          end = network.waypoint(edge, from);
        const p = new THREE.Vector3(at.x, at.y, at.z),
          yaw = Math.atan2(p.x - end.x, p.z - end.z),
          q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
        if (
          physics.ground(p, 0.4, 0.6) &&
          physics.occupiedBoxClear(
            { x: p.x, y: p.y + spec.height / 2 + 0.15, z: p.z },
            { x: spec.width / 2 + 0.18, y: spec.height / 2, z: spec.length / 2 + 0.2 },
            q,
            { x: 0, y: 0, z: 0 },
          )
        ) {
          spawn = { point: p, yaw, from };
          break;
        }
        if (attempt % 8 === 7) yield;
      }
      if (!spawn) throw new Error(`No supported clear origin for ${kind} support vehicle ${i}`);
      const car = population.createCar(`military:support:${i}`, kind, spawn.point, spawn.from);
      car.yaw = spawn.yaw;
      car.q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), car.yaw);
      car.previousQ.copy(car.q);
      car.body.setRotation(car.q, true);
      car.color = 0x70865e;
      car.name = spec.name;
      car.controlled = true;
      car.state = 'parked';
      car.speed = kind === 'tank' ? 5 : 8;
      car.cargo = 0;
      const crew = this.army.friendly.slice(personIndex, personIndex + spec.seats);
      personIndex += spec.seats;
      if (crew.length !== spec.seats)
        throw new Error('Support assignment exceeds the original 600-person roster');
      for (const [seat, soldier] of crew.entries()) {
        civil.resume(soldier.person, 'street');
        soldier.person.controlled = true;
        soldier.state = 'aboard';
        soldier.vehicle = car;
        soldier.seat = seat;
        soldier.person.carrier = car.id;
        soldier.role =
          seat === 0
            ? 'support driver'
            : seat === 1 && spec.gun
              ? 'support gunner'
              : kind === 'troop-truck'
                ? 'supply transport'
                : 'facility transport';
      }
      const stops =
        kind === 'troop-truck'
          ? [stores[i % Math.max(1, stores.length)], clinic[i % Math.max(1, clinic.length)]]
          : kind === 'military-sedan'
            ? [
                headquarters[i % Math.max(1, headquarters.length)],
                clinic[(i + 1) % Math.max(1, clinic.length)],
              ]
            : [
                stores[i % Math.max(1, stores.length)],
                headquarters[(i + 1) % Math.max(1, headquarters.length)],
              ];
      if (stops.some((p) => !p)) throw new Error(`Support ${kind} needs two working facility destinations`);
      this.units.push({
        car,
        crew,
        stops,
        stop: 0,
        state: 'dispatch',
        retry: this.army.time + i * 2,
        sequence: 0,
        visits: 0,
        reason: 'Original crew preparing a facility journey',
      });
      yield;
    }
    if (personIndex !== 600)
      throw new Error('Support roster allocation does not reconcile to 600 original IDs');
  }
  private route(unit: SupportUnit): boolean {
    const { population, physics } = this.army,
      car = unit.car,
      place = unit.stops[unit.stop],
      network = population.network;
    if (!population.civil!.network.available(place)) return false;
    for (const approach of network.approaches(place, true, car.length, 8)) {
      const edge = approach.edge,
        a = network.nodes.get(edge.from)!,
        b = network.nodes.get(edge.to)!,
        span = Math.hypot(b.x - a.x, b.z - a.z),
        dx = (b.x - a.x) / span,
        dz = (b.z - a.z) / span;
      for (const along of [0, 1, -1, 2, -2, 3, -3])
        for (const sign of [1, -1]) {
          const roadPoint = new THREE.Vector3(
            approach.point.x,
            approach.point.y,
            approach.point.z,
          ).addScaledVector(new THREE.Vector3(dx, 0, dz), along * (car.length + 3));
          const longitudinal = (roadPoint.x - a.x) * dx + (roadPoint.z - a.z) * dz;
          if (longitudinal < car.length / 2 + 8 || longitudinal > span - car.length / 2 - 8) continue;
          const point = new THREE.Vector3(a.x + dx * longitudinal, roadPoint.y, a.z + dz * longitudinal).add(
            new THREE.Vector3(-dz, 0, dx).multiplyScalar(
              sign * (edge.width / 2 + (edge.pavementWidth ?? 1.5) + car.width / 2 + 0.6),
            ),
          );
          if (
            Math.min(Math.hypot(point.x - a.x, point.z - a.z), Math.hypot(point.x - b.x, point.z - b.z)) <
            car.length / 2 + 7
          )
            continue;
          if (
            this.units.some(
              (other) =>
                other !== unit &&
                other.target &&
                other.state !== 'unavailable' &&
                Math.hypot(other.target.x - point.x, other.target.z - point.z) <
                  (other.car.length + car.length) / 2 + 2,
            )
          )
            continue;
          const floor = physics.ground(point, 0.7, 1);
          if (!floor) continue;
          point.y = floor.y;
          const yaw = Math.atan2(-dx, -dz),
            q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw),
            spec = MILITARY_SPECS[car.kind as MilitaryVehicleKind];
          if (
            !physics.occupiedBoxClear(
              { x: point.x, y: point.y + spec.height / 2 + 0.2, z: point.z },
              { x: car.width / 2 + 0.1, y: spec.height / 2, z: car.length / 2 + 0.2 },
              q,
              { x: 0, y: 0, z: 0 },
              car.body,
            )
          )
            continue;
          const plan = population.plannedRoute(car, { ...roadPoint, edge: edge.id }, approach.node, true);
          if (!plan.length) continue;
          plan.push({ x: point.x, y: point.y, z: point.z, maxSpeed: 1.6 });
          car.plan = plan;
          car.state = 'enroute';
          car.parkingYaw = yaw;
          car.think = 0;
          unit.target = point;
          unit.state = 'travelling';
          unit.reason = `Transporting the original crew to ${place.name}`;
          return true;
        }
    }
    return false;
  }
  mobilise(): void {
    for (const u of this.units) {
      u.state = 'mobilised';
      u.car.speed = 0;
      u.reason = 'Original support crew mobilising';
    }
  }
  standDown(): void {
    for (const u of this.units)
      if (u.state !== 'unavailable') {
        u.state = 'boarding';
        u.task = undefined;
        u.retry = 0;
        u.reason = 'Surviving original support crew returning';
      }
  }
  update(dt: number): void {
    const { population, physics, time } = this.army,
      civil = population.civil;
    if (this.epoch !== physics.life.epoch || !civil) return;
    for (const u of this.units) {
      const worker = u.crew[2],
        car = u.car;
      if (car.state === 'wreck' || u.crew[0].person.fatal || u.crew[0].person.state === 'incapacitated') {
        u.state = 'unavailable';
        car.speed = 0;
        continue;
      }
      if (u.state === 'mobilised') {
        if (car.incident?.startsWith('invasion:') && car.plan.length) continue;
        if (car.actualSpeed > 0.1) continue;
        car.plan = [];
        car.state = 'parked';
        if (time >= u.retry) {
          const soldier = u.crew.find(
            (s) =>
              s.state === 'aboard' &&
              s.seat !== 0 &&
              !this.army.isGunner(s) &&
              !s.person.fatal &&
              s.person.state !== 'incapacitated',
          );
          if (soldier && this.army.beginExit(soldier)) u.retry = time + 0.85;
        }
        continue;
      }
      if (u.state === 'dispatch' && time >= u.retry) {
        u.retry = time + 5;
        this.route(u);
      }
      if (u.state === 'travelling' && car.state === 'deployed') {
        if (!u.target || car.p.distanceTo(u.target) > 1) {
          u.state = 'dispatch';
          continue;
        }
        u.state = 'working';
        u.reason = 'Original worker disembarking for the reserved facility interaction';
      }
      if (u.state === 'working') {
        if (worker.person.fatal || worker.person.state === 'incapacitated') {
          u.state = 'unavailable';
          continue;
        }
        if (worker.state === 'aboard') {
          this.army.beginExit(worker);
          continue;
        }
        if (worker.transition) continue;
        if (!u.task) {
          if (time < u.retry || !civil.prepareForTask(worker.person)) continue;
          u.retry = time + 3;
          const token = `military-support:${car.id}:${++u.sequence}`,
            intended = u.stops[u.stop];
          // Several real workstations serve the same facility. An occupied
          // desk is not a reason to leave an original support worker waiting
          // forever at its parked APC; reserve another valid matching station.
          const places = (this.army.city.places ?? [])
            .filter(
              (place) =>
                place.buildingId === intended.buildingId &&
                place.floor === intended.floor &&
                place.kind === intended.kind &&
                place.id !== u.avoidPlace,
            )
            .sort(
              (a, b) =>
                Math.hypot(a.x - worker.person.p.x, a.z - worker.person.p.z) -
                Math.hypot(b.x - worker.person.p.x, b.z - worker.person.p.z),
            );
          for (const place of places)
            if (
              civil.assignTask(
                worker.person,
                place,
                token,
                car.kind === 'troop-truck'
                  ? u.stop
                    ? 'delivering supplies'
                    : 'collecting supplies'
                  : 'inspecting',
                7,
              )
            ) {
              u.task = token;
              u.stops[u.stop] = place;
              u.taskProgress = { point: worker.person.p.clone(), at: time };
              worker.task = token;
              worker.person.carry = car.kind === 'troop-truck' && u.stop ? 'equipment crate' : '';
              break;
            }
          continue;
        }
        const task = civil.task(worker.person);
        if (task?.token !== u.task || task.state === 'failed') {
          u.avoidPlace = u.stops[u.stop].id;
          u.task = undefined;
          u.taskProgress = undefined;
          u.retry = time + 2;
          continue;
        }
        if (task.state !== 'completed') {
          if (!u.taskProgress || worker.person.p.distanceToSquared(u.taskProgress.point) > 0.75 ** 2)
            u.taskProgress = { point: worker.person.p.clone(), at: time };
          else if (time - u.taskProgress.at > 25) {
            civil.cancelTask(
              worker.person,
              'Occupied facility approach stalled; reserving another matching workstation',
            );
            u.avoidPlace = u.stops[u.stop].id;
            u.task = undefined;
            u.taskProgress = undefined;
            u.retry = time + 2;
          }
          continue;
        }
        if (car.kind === 'troop-truck') {
          const store = u.stops[u.stop].buildingId!;
          if (u.stop === 0) {
            const take = Math.min(8, this.stock.get(store) ?? 0);
            this.stock.set(store, (this.stock.get(store) ?? 0) - take);
            car.cargo = (car.cargo ?? 0) + take;
          } else {
            const amount = car.cargo ?? 0;
            this.stock.set(store, (this.stock.get(store) ?? 0) + amount);
            car.cargo = 0;
            this.delivered += amount;
          }
        }
        u.visits++;
        worker.checks++;
        worker.person.carry = '';
        civil.cancelTask(worker.person, 'Facility interaction completed; returning to original transport');
        worker.task = '';
        worker.state = 'returning';
        u.task = undefined;
        u.taskProgress = undefined;
        u.avoidPlace = undefined;
        u.state = 'boarding';
        u.reason = 'Facility work complete; original worker returning';
      }
      if (u.state === 'boarding') {
        let ready = true;
        for (const soldier of u.crew)
          if (
            !soldier.person.fatal &&
            soldier.person.state !== 'incapacitated' &&
            soldier.state !== 'aboard'
          ) {
            if (!this.army.returnToVehicle(soldier, dt)) ready = false;
          }
        if (ready) {
          u.stop = (u.stop + 1) % u.stops.length;
          u.state = 'dispatch';
          u.retry = time + 2;
          car.speed = car.kind === 'tank' ? 5 : 8;
        }
      }
    }
  }
  snapshot(): Record<string, number> {
    return {
      militarySupportVehicles: this.units.length,
      militarySupportPersonnel: this.units.reduce((n, u) => n + u.crew.length, 0),
      militaryFacilityVisits: this.units.reduce((n, u) => n + u.visits, 0),
      militarySuppliesDelivered: this.delivered,
    };
  }
  inspect(): unknown {
    return {
      ...this.snapshot(),
      stock: [...this.stock],
      units: this.units.map((u) => ({
        id: u.car.id,
        kind: u.car.kind,
        state: u.state,
        crew: u.crew.map((s) => s.id),
        position: u.car.p.toArray(),
        parkingTarget: u.target?.toArray(),
        speed: u.car.actualSpeed,
        blocked: u.car.blockedBy,
        visits: u.visits,
        cargo: u.car.cargo,
        reason: u.reason,
      })),
    };
  }
}
